/**
 * Transaction Service (Frontend)
 * Manages financial transactions in Firestore
 * 
 * FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
 * 
 * This service creates transactions and optionally routes to backend API
 * for atomic envelope updates when budgets are active.
 */

import { db, auth } from '@/config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  runTransaction,
  increment
} from 'firebase/firestore';
import { CATEGORY_TYPES } from '@/constants/categoryTaxonomy';
import { normalizeCategoryKey, getActiveBudget, findEnvelopeByCategoryId } from '@/services/budgetService';
import { mapLegacyToCanonicalCategoryId } from '@/services/categoryRegistryService';

const COLLECTIONS = {
  TRANSACTIONS: 'transactions',
};

// =============================================================================
// PHASE 1: SAFE STRING HELPER
// Never write undefined to Firestore - always null for missing values
// =============================================================================
function safeString(v) {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * Get user's transactions collection reference
 * @param {String} userId 
 * @returns {CollectionReference}
 */
function getUserTransactionsRef(userId) {
  if (!userId) throw new Error('User ID required for transactions.');
  return collection(db, 'users', userId, COLLECTIONS.TRANSACTIONS);
}

/**
 * Create a new transaction with atomic envelope update
 * FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
 * 
 * @param {Object} transactionData - The raw data from the form.
 * @returns {Promise<Object>} The newly created transaction document with its ID.
 */
export async function createTransaction(transactionData) {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    throw new Error('User not authenticated. Please log in.');
  }

  //  1. Validate and build the payload
  console.log('[TRANSACTION SERVICE] ===== AMOUNT CORRUPTION DEBUG =====');
  console.log('[TRANSACTION SERVICE] RAW transactionData.amount:', transactionData.amount);
  console.log('[TRANSACTION SERVICE] typeof transactionData.amount:', typeof transactionData.amount);
  console.log('[TRANSACTION SERVICE] JSON.stringify(transactionData.amount):', JSON.stringify(transactionData.amount));
  
  // CRITICAL FIX: Round to 2 decimal places to avoid floating point precision issues
  const rawAmount = Number(transactionData.amount);
  const amount = Math.round(rawAmount * 100) / 100;  // Round to cents, then back to dollars
  
  console.log('[TRANSACTION SERVICE] After Number() conversion (raw):', rawAmount);
  console.log('[TRANSACTION SERVICE] After rounding fix:', amount);
  console.log('[TRANSACTION SERVICE] typeof amount:', typeof amount);
  console.log('[TRANSACTION SERVICE] amount.toFixed(10):', amount.toFixed(10));
  console.log('[TRANSACTION SERVICE] Expected (if 10000):', 10000);
  console.log('[TRANSACTION SERVICE] Difference from 10000:', (10000 - amount).toFixed(10));
  console.log('[TRANSACTION SERVICE] Ratio to 10000:', (amount / 10000).toFixed(20));
  console.log('[TRANSACTION SERVICE] 1/1024 deduction test:', Math.abs(amount - 10000 * (1023/1024)) < 0.01 ? '✓ YES, IT IS 1/1024!' : '✗ NO');
  console.log('[TRANSACTION SERVICE] ===== END AMOUNT DEBUG =====');
  
  if (Number.isNaN(amount) || amount <= 0) {
    throw new Error('Invalid amount provided. Amount must be a positive number.');
  }

  if (!transactionData.categoryId) {
    throw new Error('Category is required.');
  }

  const date = transactionData.date ? new Date(transactionData.date) : new Date();
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date provided.');
  }

  // FINLIST SSOT: Map to canonical category ID for consistent envelope matching
  // EXCEPTION: Custom categories (starting with 'custom-') should be preserved as-is
  let canonicalCategoryId;
  const isCustomCategory = transactionData.categoryId?.startsWith('custom-');
  
  if (isCustomCategory) {
    // Custom categories are preserved exactly for matching with custom envelopes
    canonicalCategoryId = transactionData.categoryId;
  } else {
    try {
      canonicalCategoryId = mapLegacyToCanonicalCategoryId(transactionData.categoryId);
    } catch {
      // Fallback to legacy normalization if new service not available
      canonicalCategoryId = normalizeCategoryKey(transactionData.categoryId);
    }
  }

  // ==========================================================================
  // PHASE 1: Build payload with safe string handling
  // RULE: Never write undefined fields to Firestore
  // ==========================================================================
  const safeEnvelopeId = safeString(transactionData.envelopeId);
  const safeCategoryId = safeString(canonicalCategoryId);
  const safeOriginalCategoryId = safeString(transactionData.categoryId);
  
  // DEV WARNING: If user selected an envelope but it's not being written
  if (import.meta.env?.DEV) {
    if (transactionData.envelopeId && !safeEnvelopeId) {
      console.warn('[TRANSACTION SERVICE] ⚠️ envelopeId was provided but is invalid:', transactionData.envelopeId);
    }
    // Log envelope tracking for debugging
    console.log('[TRANSACTION SERVICE] Envelope tracking:', {
      inputEnvelopeId: transactionData.envelopeId,
      safeEnvelopeId,
      inputCategoryId: transactionData.categoryId,
      canonicalCategoryId: safeCategoryId,
      isCustomCategory
    });
  }

  // Compute month key (YYYY-MM format) for TransactionsPage filtering
  const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

  const payload = {
    amount,
    // PHASE 1: Safe category fields - never undefined
    categoryId: safeCategoryId,
    originalCategoryId: safeOriginalCategoryId,
    isCustomCategory: isCustomCategory || false,
    categoryType: transactionData.categoryType || CATEGORY_TYPES.EXPENSE,
    type: (transactionData.categoryType || CATEGORY_TYPES.EXPENSE).toLowerCase(),
    date: Timestamp.fromDate(date),
    month: monthKey, // REQUIRED: Used by TransactionsPage to filter by month
    description: safeString(transactionData.description),
    notes: safeString(transactionData.notes),
    
    // Unified category fields (for consistency with other modules)
    subcategoryId: safeString(transactionData.subcategoryId),
    customCategory: safeString(transactionData.customCategory),
    customSubcategory: safeString(transactionData.customSubcategory),
    
    // ==========================================================================
    // PHASE 1: CRITICAL - envelopeId is PRIMARY for budget rollup
    // This MUST be written when user selects an envelope
    // ==========================================================================
    envelopeId: safeEnvelopeId,
    budgetId: safeString(transactionData.budgetId), // Will be set if envelope update happens
    
    // Metadata
    userId: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    source: safeString(transactionData.source) || 'web-manual',
    archived: false,
  };

  // 2. Log the payload for debugging
  console.log("[TRANSACTION SERVICE] ===== FIRESTORE PAYLOAD DEBUG =====");
  console.log("[TRANSACTION SERVICE] payload.amount:", payload.amount);
  console.log("[TRANSACTION SERVICE] typeof payload.amount:", typeof payload.amount);
  console.log("[TRANSACTION SERVICE] payload.amount.toFixed(10):", payload.amount.toFixed(10));
  console.log("[TRANSACTION SERVICE] payload.envelopeId:", payload.envelopeId);
  console.log("[TRANSACTION SERVICE] Full payload:", JSON.stringify(payload, null, 2));
  console.log("[TRANSACTION SERVICE] ===== END PAYLOAD DEBUG =====");

  // 3. FINLIST SSOT: Try to create with atomic envelope update
  const isExpense = payload.type === 'expense';
  
  if (isExpense) {
    try {
      const result = await createTransactionWithEnvelopeUpdate(userId, payload, canonicalCategoryId);
      if (result) {
        return result;
      }
    } catch (envelopeError) {
      console.warn('[TRANSACTION SERVICE] Atomic envelope update failed, falling back:', envelopeError.message);
    }
  }

  // 4. Fallback: Direct Firestore write (no envelope update)
  try {
    const docRef = await addDoc(getUserTransactionsRef(userId), payload);
    console.log('[TRANSACTION SERVICE] Transaction created (no envelope update):', docRef.id);
    return { id: docRef.id, ...transactionData, createdAt: new Date() };
  } catch (error) {
    console.error("[TRANSACTION SERVICE] Firestore write error:", {
      message: error.message,
      code: error.code,
      payload: payload,
    });
    throw new Error('Could not save the transaction to the database. Please try again.');
  }
}

/**
 * Create transaction with atomic envelope update using Firestore runTransaction
 * FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
 * 
 * @param {string} userId - User ID
 * @param {Object} payload - Transaction payload
 * @param {string} canonicalCategoryId - Canonical category ID
 * @returns {Promise<Object|null>} Transaction result or null if envelope not found
 */
async function createTransactionWithEnvelopeUpdate(userId, payload, canonicalCategoryId) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  
  // Get active budget
  const activeBudget = await getActiveBudget(userId);
  if (!activeBudget?.id) {
    console.log('[TRANSACTION SERVICE] No active budget found');
    return null;
  }

  // Find envelope for this category
  const envelope = await findEnvelopeByCategoryId(userId, activeBudget.id, canonicalCategoryId);
  if (!envelope?.id) {
    console.log('[TRANSACTION SERVICE] No envelope found for category:', canonicalCategoryId);
    return null;
  }

  // Update payload with budget/envelope info
  payload.envelopeId = envelope.id;
  payload.budgetId = activeBudget.id;

  // Execute atomic transaction
  const transactionId = await runTransaction(db, async (transaction) => {
    // References
    const transactionRef = doc(getUserTransactionsRef(userId));
    const envelopeRef = doc(db, 'users', userId, 'budgets', activeBudget.id, 'lines', envelope.id);
    
    // Write transaction document
    transaction.set(transactionRef, payload);
    
    // Atomically update envelope
    transaction.update(envelopeRef, {
      actual: increment(payload.amount),
      remaining: increment(-payload.amount),
      updatedAt: serverTimestamp()
    });
    
    return transactionRef.id;
  });

  console.log('[TRANSACTION SERVICE] Transaction created with atomic envelope update:', {
    transactionId,
    envelopeId: envelope.id,
    budgetId: activeBudget.id,
    amount: payload.amount
  });

  return { 
    id: transactionId, 
    ...payload, 
    envelopeId: envelope.id,
    budgetId: activeBudget.id,
    createdAt: new Date() 
  };
}

/**
 * Get all transactions for user
 * @param {String} userId
 * @param {Object} filters - { startDate, endDate, categoryId, type }
 * @returns {Promise<Array>}
 */
export async function getTransactions(userId = null, filters = {}) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  let q = query(
    getUserTransactionsRef(uid),
    where('archived', '==', false),
    orderBy('date', 'desc')
  );

  // Apply filters
  if (filters.startDate) {
    q = query(q, where('date', '>=', filters.startDate));
  }
  if (filters.endDate) {
    q = query(q, where('date', '<=', filters.endDate));
  }
  if (filters.categoryId) {
    q = query(q, where('categoryId', '==', filters.categoryId));
  }
  if (filters.categoryType) {
    q = query(q, where('categoryType', '==', filters.categoryType));
  }
  if (filters.limit) {
    q = query(q, limit(filters.limit));
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Get transaction by ID
 * @param {String} transactionId
 * @returns {Promise<Object|null>}
 */
export async function getTransaction(transactionId) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('User not authenticated');

  const docRef = doc(getUserTransactionsRef(userId), transactionId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Update transaction
 * @param {String} transactionId
 * @param {Object} updates
 * @returns {Promise<Object>}
 */
export async function updateTransaction(transactionId, updates) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('User not authenticated');

  const docRef = doc(getUserTransactionsRef(userId), transactionId);
  const updateData = {
    ...updates,
    updatedAt: serverTimestamp()
  };

  await updateDoc(docRef, updateData);
  return { id: transactionId, ...updateData };
}

/**
 * Delete transaction (soft delete - archive)
 * @param {String} transactionId
 * @returns {Promise<void>}
 */
export async function deleteTransaction(transactionId) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('User not authenticated');

  const docRef = doc(getUserTransactionsRef(userId), transactionId);
  await updateDoc(docRef, {
    archived: true,
    updatedAt: serverTimestamp()
  });
}

function getMonthKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Get transactions for the current calendar month
 */
export async function getCurrentMonthTransactions(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return getTransactions(uid, {
    startDate: startOfMonth.toISOString(),
    endDate: now.toISOString()
  });
}

/**
 * Calculate a simple summary from a transaction list
 */
export function calculateSummary(transactions = []) {
  return transactions.reduce((acc, transaction) => {
    const amount = Number(transaction.amount) || 0;
    const type = (transaction.categoryType || transaction.type || '').toLowerCase();

    if (type === CATEGORY_TYPES.INCOME || type === 'income') {
      acc.income += amount;
      acc.balance += amount;
    } else {
      acc.expenses += amount;
      acc.balance -= amount;
    }

    return acc;
  }, {
    balance: 0,
    income: 0,
    expenses: 0
  });
}

/**
 * Aggregate spending per category id
 */
export function getSpendingByCategory(transactions = []) {
  return transactions.reduce((acc, transaction) => {
    const amount = Number(transaction.amount) || 0;
    const type = (transaction.categoryType || transaction.type || '').toLowerCase();

    if (type !== CATEGORY_TYPES.EXPENSE && type !== 'expense') {
      return acc;
    }

    const key = transaction.categoryId || transaction.category?.id || 'uncategorized';
    acc[key] = (acc[key] || 0) + amount;
    return acc;
  }, {});
}

/**
 * Get transactions within a date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Array>} Transactions in the date range
 */
export async function getTransactionsByDateRange(userId, startDate, endDate) {
  return getTransactions(userId, { startDate, endDate });
}
